// v1_804: the client's side of following one document engine run. Shared so the
// harness (tools/harness-df-run-poll.mjs) runs the code the portal runs.
import { analysisRunState } from './next-action.js';

// v1_804: START THE DOCUMENT ENGINE AND REMEMBER WHICH RUN IT IS. The start
// answers 202 with the runId; the page's completion poll follows that run
// alone, so an earlier run's complete line still on the strip never reads as
// this run finishing. Until the answer arrives the run counts as starting.
export function startDfRun(runRef, body, fetchImpl = (...a) => fetch(...a)) {
  const since = Date.now();
  runRef.current = { id: null, pending: true, since };
  const mine = () => runRef.current && runRef.current.since === since;
  try {
    fetchImpl('/api/df-process', { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(r => r.json().catch(() => ({})))
      .then(d => { if (mine()) runRef.current = (d && d.runId) ? { id: d.runId, since } : null; })
      .catch(() => { if (mine()) runRef.current = null; });
  } catch (_) { runRef.current = null; }
}
// The completion rule for the run this page started (or the whole strip when
// it started none, or the start never answered within five minutes).
export function dfRunStateFor(runRef, progress) {
  const r = runRef.current;
  if (r && r.pending && Date.now() - r.since < 5 * 60 * 1000) return { running: true, stopped: false, complete: false };
  return analysisRunState(progress, Date.now(), (r && r.id) ? { runId: r.id, since: r.since } : {});
}

// v1_804: WHICH OF THE FIVE STAGES A RUN IS ON, read from the strip for the
// dashboard's banner. The newest handoff names the stage it handed to, and
// each stage's own lines name it too (a stage run in the same call as the one
// before has no handoff); the later of the two is where the run is. `since` is
// when that stage was handed on or claimed, where the strip says so (null when
// it does not; the banner then counts from when it first saw the stage).
export const ENGINE_STAGES = [
  { key: 'read', name: 'Reading the documents' },
  { key: 'assemble', name: 'Assembling the case' },
  { key: 'fold', name: 'Folding in the conversation and case notes' },
  { key: 'finishA', name: 'Screening and checking the whole file' },
  { key: 'finishB', name: 'Scoring the risk and saving the file' },
];
export function engineStage(progress) {
  const ap = Array.isArray(progress) ? progress.filter(Boolean) : [];
  if (!ap.length) return null;
  const idx = (k) => ENGINE_STAGES.findIndex(s => s.key === k);
  const failed = ap.find(e => e.status === 'failed');
  const handoff = ap.find(e => e.key === 'handoff');
  const handed = handoff && handoff.meta && handoff.meta.next ? idx(handoff.meta.next) : -1;
  let fromLines = -1;
  if (ap.some(e => e.key === 'reading' || e.key === 'prescreen' || String(e.key).startsWith('doc:'))) fromLines = 0;
  if (ap.some(e => e.key === 'assemble')) fromLines = 1;
  if (ap.some(e => e.key === 'screening')) fromLines = 3;
  if (ap.some(e => e.key === 'checkpoint' && e.status === 'done')) fromLines = 4;
  // A stage that did not start is named by its failed line.
  const failedAt = failed && failed.meta && failed.meta.stage ? idx(failed.meta.stage) : -1;
  const i = failedAt >= 0 ? failedAt : Math.max(handed, fromLines, 0);
  const s = ENGINE_STAGES[i];
  const t = handoff && handed === i && handoff.at ? Date.parse(handoff.at) : NaN;
  return { key: s.key, name: s.name, number: i + 1, of: ENGINE_STAGES.length, since: Number.isFinite(t) ? t : null, didNotStart: failedAt >= 0 };
}
// Minutes and seconds, for "3 min 20 s on this stage".
export function formatElapsed(ms) {
  const s = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const m = Math.floor(s / 60);
  return m ? `${m} min ${s % 60} s` : `${s} s`;
}

// v1_805: THE BROWSER DRIVES THE RUN. A stage that hands on records the next
// stage as pending (the strip's handoff line, active, naming it). The page
// following the run sends `advance` for it; the engine claims the stage (create
// only) and runs it. The engine sweep cron is the backstop when no page is open.
export function pendingStage(progress) {
  const ap = Array.isArray(progress) ? progress : [];
  const h = ap.find(e => e && e.key === 'handoff' && e.status === 'active');
  if (!h || !h.meta || !h.meta.next) return null;
  return { stage: h.meta.next, runId: h.meta.runId || h.runId || null, seq: h.meta.seq || 0, at: Date.parse(h.at) || 0 };
}
// One driver per page: call it with each strip read. It sends advance at most
// once per pending stage while that request is open (the request stays open
// while the stage runs), and again only if a request ended and the same stage
// is still pending after retryMs (a dropped connection). Returns the stage it
// advanced, or null.
export function makeRunDriver(sendAdvance, retryMs = 20000) {
  const sent = new Map();
  return (progress) => {
    const p = pendingStage(progress);
    if (!p) return null;
    const key = `${p.runId}:${p.seq}`;
    const s = sent.get(key);
    if (s && (s.open || Date.now() - s.at < retryMs)) return null;
    const rec = { at: Date.now(), open: true };
    sent.set(key, rec);
    Promise.resolve().then(() => sendAdvance(p)).catch(() => {}).finally(() => { rec.open = false; rec.at = Date.now(); });
    return p;
  };
}
